import angular from 'angular';
import { setTranslations, changeLang } from '@sdk/lang/gettext';
import { IntegrationPlatform } from '@sdk/integrations/types';

import { langs } from '../lang/constants';

import dayHtml from './ui-bootstrap/datepicker/day.html';
import monthHtml from './ui-bootstrap/datepicker/month.html';
import menuUrl from './menu/menu.html';
import config from './config';

export default function appRun(
  $rootScope,
  $state,
  $uibModal,
  $http,
  $interval,
  $transitions,
  $location,
  $localStorage,
  $anchorScroll,
  $window,
  $q,
  $document,
  $templateCache,
  $timeout,
  $injector,
  AccountService,
  Authentication,
  Socket,
  AlertService,
  StatusService,
  Restangular,
  Title,
  IgnoreServerErrors,
  ModalStack,
  AllModels,
  PersonaCollection,
  MessageNotification,
  NewsfeedCollection,
  TaskCollection,
  EventCollection,
  NotificationCollection,
  IntercomService,
  HeadroomService,
  SalesService,
  RefreshData,
  LeaveService,
  IntegrationService,
  AnnouncementService,
  AppBootstrap,
  SlingAnalytics,
  WindowLocation,
  gettextCatalog
) {
  'ngInject';

  window.__Promise = window.Promise;
  window.Promise = $q;
  window.Promise.allSettled = window.__Promise.allSettled;

  if (__DEV__) {
    $window.angular = angular;
    $window.$rootScope = $rootScope;
    $window.$q = $q;
    $window.Authentication = Authentication;
    $window.$injector = $injector;
    $window.HTMLElement.prototype.__scope = function () {
      // eslint-disable-line
      return angular.element(this).scope();
    };
  }

  ModalStack.init();

  // Init the authentication module: loads the token and user from local storage
  Authentication.init(() => {
    IntegrationService.init();

    if (Authentication.user) {
      initLanguage();
      SlingAnalytics.track(SlingAnalytics.analyticsEvents.userOpened);
      StatusService.refresh();
      LeaveService.loadTimeOffTypes();
      // Intercom: do not call _refreshExternalServicesData() here. It would trigger
      // a /intercom/user fetch before init() runs; init() (from userLoaded.then)
      // already fetches JWT. We only refresh Intercom on login (event:auth-loginConfirmed).
    }
  });
  // eslint-disable-next-line complexity
  Authentication.userLoaded.then(async () => {
    await IntercomService.init();

    const { linkToken, code, autosetup, origin, fromToast, entitlements } = $location.search();

    const { onboarding } = Authentication.user.settings.clientFlags;
    let syncUserSettings = false;

    // skip position and user settings for Shift4 integration
    if (linkToken) {
      Authentication.user.settings.clientFlags.onboarding.skipSteps = {
        ...onboarding.skipSteps,
        positions: true,
        employees: true,
      };
      syncUserSettings = true;
    }

    if (code || autosetup) {
      Authentication.user.settings.clientFlags.onboarding = {
        finished: true,
        skipSteps: { account: true },
      };
      syncUserSettings = true;
    }

    if (origin) {
      Authentication.user.settings.clientFlags.signupOrigin = origin;
    } else if (fromToast || entitlements) {
      Authentication.user.settings.clientFlags.signupOrigin = IntegrationPlatform.toast;
    }

    if (syncUserSettings) {
      await Authentication.syncSettings();
    }

    if (
      Authentication.user.status === 'unverified' &&
      !Authentication.user.settings.clientFlags.onboarding.finished &&
      !Authentication.user.settings.clientFlags.signupOrigin
    ) {
      $rootScope.verifyEmailCoverShown = true;
      return;
    }

    if (IntercomService.shouldAutoactivate()) {
      IntercomService.activate();
    }

    await AnnouncementService.show();
    await $timeout(5000);

    AccountService.showAccountRelatedModalsOnRefresh();
  });

  // Override ui-bootstrap's datepicker template
  $templateCache.put('uib/template/datepicker/day.html', dayHtml);
  $templateCache.put('uib/template/datepicker/month.html', monthHtml);

  $rootScope.menuUrl = menuUrl;
  $rootScope.socketConnection = true;
  $rootScope.navbarHeight = 60;
  $rootScope.FEET_IN_METERS = 0.3048;
  $rootScope.isMacOS = navigator.userAgent.includes('Mac OS X');
  $rootScope.cloudinaryBaseUrl = config.CLOUDINARY_BASE_URL;
  $rootScope.STAGING = __STAGING__;
  $rootScope.DEV = __DEV__;
  $rootScope.authentication = Authentication;
  $rootScope.config = config;
  $rootScope.statusService = StatusService;
  $rootScope.$state = $state;
  $rootScope.Socket = Socket;
  $rootScope.updateAvailable = false;
  $rootScope.maintenanceMode = false;
  $rootScope.debug = __DEV__;
  $rootScope.isToday = isToday;
  $rootScope.isPositionStickySupported = isPositionStickySupported();

  $rootScope.$on('$viewContentLoaded', () => {
    $anchorScroll();
  });

  $rootScope.$on('opening-modal', () => {
    AlertService.clear();
  });

  $rootScope.$on('event:auth-loginRequired', () => {
    Authentication.logout(true, true);
    WindowLocation.href = `${config.SLING_LOGIN_URL}?redirect=${encodeURIComponent(
      WindowLocation.href
    )}`;
  });

  $rootScope.$on('event:auth-loginConfirmed', () => {
    // Login to the websocket server
    if (Authentication.token) {
      console.log(`Login to Cloak with token ${Authentication.token}`);
      Socket.send({
        command: 'join',
        room: Authentication.token,
      });
    }

    // Clear all alerts
    AlertService.clear();

    // Refresh notifications
    StatusService.refresh();

    _refreshExternalServicesData();
  });

  function _refreshExternalServicesData() {
    IntercomService.updateUser(Authentication.user, Authentication.intercomUserHash);
  }

  AppBootstrap.setupWebSockets();
  AppBootstrap.setupVersionAndMaintenanceWatch();
  AppBootstrap.refreshOnTimezoneChange();
  AppBootstrap.setupGlobalTransitionsHooks();
  AppBootstrap.setupCollections();
  AppBootstrap.setupPremiumDiscoveryService();

  $window.addEventListener('online', () => {
    Socket.reconnect(true);
  });

  $window.addEventListener('offline', () => {
    $rootScope.socketConnection = false;
    Socket.reconnect();
  });

  Object.assign(HeadroomService.options, {
    onPin() {
      $rootScope.$emit('headroom:toggled', true);
    },
    onUnpin() {
      $rootScope.$emit('headroom:toggled', false);
    },
  });

  Object.defineProperty($rootScope, 'isFullscreen', {
    get() {
      return $document[0].fullscreenElement;
    },
  });

  function initLanguage() {
    setTranslations(langs);

    let currentLang = 'en';
    if (config.I18N_ENABLED) {
      currentLang = Authentication.user.settings.language || 'en';
    }

    gettextCatalog.currentLanguage = currentLang;
    changeLang(currentLang);
  }

  function isPositionStickySupported() {
    const document = $document[0];
    const span = document.createElement('span');
    span.style.position = '-webkit-sticky';
    span.style.position = 'sticky';

    return span.style.position.includes('sticky');
  }
}

function isToday(date) {
  const today = new Date();

  if (date && angular.isObject(date)) {
    return date.toDateString() === today.toDateString();
  }

  if (date && angular.isString(date)) {
    const dateObj = new Date(date);
    if (dateObj.toString() === 'Invalid Date') {
      return false;
    }

    return dateObj.toDateString() === today.toDateString();
  }

  return false;
}
